import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/socialLinksService";
import type { SocialLink } from "../types";

interface SocialLinksState {
  items: SocialLink[];
  loading: boolean;
  error: string | null;
}

const initialState: SocialLinksState = { items: [], loading: false, error: null };

export const fetchSocialLinks = createAsyncThunk("cms/socialLinks/fetch", api.listSocialLinks);
export const addSocialLink = createAsyncThunk("cms/socialLinks/add", api.createSocialLink);
export const editSocialLink = createAsyncThunk(
  "cms/socialLinks/edit",
  ({ id, payload }: { id: number; payload: Omit<SocialLink, "id"> }) => api.updateSocialLink(id, payload)
);
export const removeSocialLink = createAsyncThunk("cms/socialLinks/remove", async (id: number) => {
  await api.deleteSocialLink(id);
  return id;
});

const socialLinksSlice = createSlice({
  name: "cmsSocialLinks",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSocialLinks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSocialLinks.fulfilled, (state, action: PayloadAction<SocialLink[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchSocialLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load social links.";
      })
      .addCase(addSocialLink.fulfilled, (state, action: PayloadAction<SocialLink>) => {
        state.items.push(action.payload);
      })
      .addCase(editSocialLink.fulfilled, (state, action: PayloadAction<SocialLink>) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(removeSocialLink.fulfilled, (state, action: PayloadAction<number>) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      });
  },
});

export default socialLinksSlice.reducer;
